import {
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import { ApolloClient } from "@apollo/client/core";
import {
  ApolloLink,
  NormalizedCacheObject,
} from "@apollo/client";

import { InMemoryCache } from "@apollo/client/cache";
import { CachePersistor, LocalStorageWrapper } from "apollo3-cache-persist";
import getLinks from "./links";
import { API_URL } from "../../constants/app";
import { clearQuestionsFromCache } from "./clearQuestionsFromCache";
import { ApolloClientType } from "../../types/apollo";
import attachClient from "../../store/attachClientToStore";
import { store } from "../../store";

/**
 * To improve initial load time, create an apollo cache object as soon as
 * this module is executed, since it doesn't depend on any component props.
 * The tradeoff is that we may be creating an instance we don't end up needing.
 */
const preInstantiatedCache = new InMemoryCache({
  addTypename: false
});

const Adapter = () => {
  const [initialized, setInitialized] = useState(false);

  const apiBase = new URL("/graphql", API_URL).toString();
  const apolloLink = ApolloLink.from(Array.from(getLinks(apiBase).values()));

  const createApolloClient = useCallback((
    cache: InMemoryCache,
    link: ApolloLink
  ): ApolloClient<NormalizedCacheObject> => {
    return new ApolloClient({
      cache,
      link,
      defaultOptions: {
        query: {
          errorPolicy: "all",
        },
        mutate: {
          errorPolicy: "all",
        },
      },
    });
  }, []);

  const createCachePersistor = useCallback((cache: any): CachePersistor<NormalizedCacheObject> => {
    return new CachePersistor({
      key: "inbuonasalute",
      cache,
      storage: new LocalStorageWrapper(window.localStorage),
      debug: process.env.NODE_ENV === "development",
    });
  }, []);

  const clearCacheData = useCallback(
    async (client: ApolloClientType) => {
      await clearQuestionsFromCache(client);
    },
    []
  );

  const apolloClient = useMemo(() => {
    const client: ApolloClientType = createApolloClient(preInstantiatedCache, apolloLink);
    const persistor = createCachePersistor(preInstantiatedCache);

    client.apiBase = apiBase;
    client.persistor = persistor;
    client.clearCacheData = clearCacheData;

    return client;
  }, [
    apiBase,
    clearCacheData,
    createApolloClient,
    createCachePersistor
  ]);

  useEffect(() => {
    if (initialized) return;

    // immediately invoke this async function
    (async () => {
      // restore persisted data to the Apollo cache
      await apolloClient.persistor?.restore();

      // // attach the Apollo client to the Redux store
      await attachClient(apolloClient);

      // mark this routine as complete
      setInitialized(true);
    })();
  }, [apolloClient, initialized]);

  return {
    apolloClient,
    initialized,
    store,
  };
};

export default Adapter;
